import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CaretDown, Crown, List, SignOut, User, Users, CreditCard, Gear, Headset, FileText, ShieldWarning, ShieldCheck } from 'phosphor-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import api from '../../lib/api';
import { fetchOptionsAccount } from '../../lib/optionsApi';
import useOptionsAlertsData from '../../hooks/useOptionsAlertsData';
import { OPTIONS_OPEN_OPERATIONS_UPDATED_EVENT } from '../../lib/optionsEvents';
import { getOptionsNavigationItems } from './optionsNavigationConfig';
import { OptionsAlertsInboxButton } from './OptionsAlertsInboxButton';
import UpgradeModal, { OPTIONS_PRO_BENEFITS } from '../UpgradeModal';
import CryptoPaymentModal from '../CryptoPaymentModal';

const PRO_BRAIN_ICON = 'https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/9edc7349_justbrain.png';
const LOGO_HORIZONTAL = 'https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png';

const ProductMenu = ({ user, profile, onLogout, isDemoMode, onEnterDemo, onExitDemo, t }) => {
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-user-menu-trigger">
          {profile.isPro ? (
            <img src={PRO_BRAIN_ICON} alt="Pro User" className="h-6 w-auto object-contain opacity-90" data-testid="options-pro-brain-indicator" />
          ) : (
            <User size={22} weight="fill" className="text-white/80" data-testid="options-user-avatar" />
          )}
          <CaretDown size={14} weight="bold" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64 border-white/10 bg-[#121212] text-white" data-testid="options-user-menu-dropdown">
        <DropdownMenuLabel className="space-y-2 px-3 py-3" data-testid="options-user-menu-header">
          <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">GENI Options</p>
          <div>
            <p className="text-xs text-white/40">{t('common.signedInAs')}</p>
            <p className="truncate text-sm font-medium text-white/80" title={user?.email} data-testid="options-user-menu-email">{user?.email}</p>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/40">{t('common.plan')}</span>
            <span className="flex items-center gap-1 text-[#F0B90B]" data-testid="options-user-menu-plan">
              {profile.isPro ? <Crown size={14} weight="fill" /> : <ShieldCheck size={14} weight="duotone" />}
              {profile.planLabel}
            </span>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-white/10" />
        {isDemoMode ? (
          <DropdownMenuItem className="cursor-pointer text-[#D6E8FF] focus:bg-[#60A5FA]/10 focus:text-[#D6E8FF]" data-testid="options-menu-exit-demo" onClick={onExitDemo}>
            {t('nav.exitDemo')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem className="cursor-pointer text-[#D6E8FF] focus:bg-[#60A5FA]/10 focus:text-[#D6E8FF]" data-testid="options-menu-view-demo" onClick={onEnterDemo}>
            {t('nav.viewDemo')}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white" data-testid="options-menu-trades" onClick={() => navigate('/dashboard')}>
          {t('nav.geniTrades')}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white" data-testid="options-menu-billing" onClick={() => navigate('/billing')}>
          <CreditCard size={16} weight="duotone" /> {t('common.billing')}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white" data-testid="options-menu-referral" onClick={() => navigate('/referral')}>
          <Users size={16} weight="duotone" /> {t('common.referral')}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white" data-testid="options-menu-settings" onClick={() => navigate('/settings')}>
          <Gear size={16} weight="duotone" /> {t('common.settings')}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white" data-testid="options-menu-support" onClick={() => navigate('/support')}>
          <Headset size={16} weight="duotone" /> {t('common.support')}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white" data-testid="options-menu-terms" onClick={() => navigate('/terms')}>
          <FileText size={16} weight="duotone" /> {t('common.terms')}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white" data-testid="options-menu-privacy" onClick={() => navigate('/privacy')}>
          <ShieldWarning size={16} weight="duotone" /> {t('common.privacy')}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem className="cursor-pointer text-[#FF8A8A] focus:bg-[#FF8A8A]/10 focus:text-[#FFB3B3]" data-testid="options-menu-logout" onClick={onLogout}>
          <SignOut size={16} weight="duotone" /> {t('common.logOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const OptionsNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, toggleLanguage, isPortuguese } = useOptionsLocale();
  const { user, signOut } = useAuth();
  const { isDemoMode, enterDemoMode, exitDemoMode, mode } = useOptionsMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Utilizar o hook central de entitlements
  const { isPro, profile: centralProfile, loading, refetchProfile } = useOptionsEntitlements();
  
  const planLabel = loading
    ? '...'
    : isPro
      ? `Pro ${{ monthly: 'Monthly', yearly: 'Yearly', lifetime: 'Lifetime', beta: 'Beta' }[centralProfile?.subscription_plan] || 'Yearly'}`
      : 'Free';

  const profile = { isPro, planLabel };

  const [openOperationsCount, setOpenOperationsCount] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [cryptoInitialPlan, setCryptoInitialPlan] = useState(null);
  const { data: alertsData, markRead, busyRuleId } = useOptionsAlertsData();
  const items = useMemo(() => getOptionsNavigationItems(t), [t]);
  const optionsUpgradeContent = useMemo(() => ({
    optionsBadge: 'GENI Options PRO',
    title: isPortuguese ? 'GENI OPTIONS PRO' : 'GENI OPTIONS PRO',
    description: isPortuguese
      ? 'Desbloqueie toda a experiência de análise e acompanhamento das suas operações de Options.'
      : 'Unlock the full analysis and monitoring experience for your options operations.',
    benefitsTitle: isPortuguese ? 'BENEFÍCIOS PRO' : 'PRO BENEFITS',
    benefits: OPTIONS_PRO_BENEFITS,
    disclaimer: isPortuguese
      ? 'Os recursos incluídos nos planos podem evoluir conforme o GENI Options recebe novas funcionalidades.'
      : 'Included plan capabilities may evolve as GENI Options gains new functionality.',
    monthlyPlanLabel: isPortuguese ? 'PRO Mensal' : 'PRO Monthly',
    yearlyPlanLabel: isPortuguese ? 'PRO Anual' : 'PRO Yearly',
    monthlyIntervalLabel: isPortuguese ? '/ mês' : '/ month',
    yearlyIntervalLabel: isPortuguese ? '/ ano' : '/ year',
    monthlyButtonLabel: isPortuguese ? 'Upgrade para PRO Mensal' : 'Upgrade to PRO Monthly',
    yearlyButtonLabel: isPortuguese ? 'Upgrade para PRO Anual' : 'Upgrade to PRO Yearly',
    bestValueLabel: isPortuguese ? 'Melhor valor' : 'Best value',
    paymentNote: isPortuguese
      ? 'Pagamento seguro em BRL pelo checkout existente.'
      : 'Secure BRL payment through the existing checkout flow.',
    redirectingTitle: isPortuguese ? 'Redirecionando para o checkout...' : 'Redirecting to checkout...',
    redirectingBody: isPortuguese
      ? 'Você será redirecionado ao Stripe para concluir o pagamento com segurança.'
      : 'You will be redirected to Stripe to complete your payment securely.',
  }), [isPortuguese]);

  useEffect(() => {
    const handleStripeReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const stripeSessionId = urlParams.get('stripe_session_id');
      const paymentStatus = urlParams.get('payment_status');

      if (paymentStatus === 'canceled') {
        window.history.replaceState({}, document.title, window.location.pathname);
        toast.info(isPortuguese ? 'Pagamento cancelado.' : 'Payment was canceled.');
        return;
      }

      if (!stripeSessionId || paymentStatus !== 'success') {
        return;
      }

      window.history.replaceState({}, document.title, window.location.pathname);
      toast.loading(isPortuguese ? 'Verificando pagamento...' : 'Verifying payment...', { id: 'options-stripe-verify' });

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const pollStatus = async () => {
        try {
          const response = await api.get(`/payments/stripe/status/${stripeSessionId}`);

          if (response.data.payment_status === 'paid') {
            toast.success(isPortuguese ? 'Pagamento confirmado! Seu PRO está ativo.' : 'Payment confirmed! Your PRO is now active.', { id: 'options-stripe-verify' });
            await refetchProfile();
            return true;
          }

          if (response.data.status === 'expired') {
            toast.error(isPortuguese ? 'A sessão de pagamento expirou. Tente novamente.' : 'Payment session expired. Please try again.', { id: 'options-stripe-verify' });
            return true;
          }

          attempts += 1;
          if (attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
            return pollStatus();
          }

          const verifyResponse = await api.post('/payments/stripe/verify-pending');
          if (verifyResponse.data.verified) {
            toast.success(verifyResponse.data.message || (isPortuguese ? 'Pagamento verificado! Seu PRO está ativo.' : 'Payment verified! Your PRO is now active.'), { id: 'options-stripe-verify' });
            await refetchProfile();
          } else {
            toast.success(isPortuguese ? 'Pagamento em processamento. Seu acesso PRO será ativado em instantes.' : 'Payment is processing. Your PRO access will be activated shortly.', { id: 'options-stripe-verify' });
          }
          return true;
        } catch (error) {
          if (error.response?.status === 401 && attempts < maxAttempts) {
            attempts += 1;
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
            return pollStatus();
          }

          toast.error(isPortuguese ? 'Não foi possível verificar o pagamento agora.' : 'Could not verify payment status right now.', { id: 'options-stripe-verify' });
          return true;
        }
      };

      await pollStatus();
    };

    handleStripeReturn();
  }, [location.search, isPortuguese, refetchProfile]);

  useEffect(() => {
    let active = true;

    const loadOpenOperationsCount = async () => {
      try {
        const data = await fetchOptionsAccount();
        if (!active) return;
        const optionsOpenCount = Number(data?.trading_pnl?.totals_usd?.open_operations_count || 0);
        const managementOpenCount = Array.isArray(data?.management_positions) ? data.management_positions.length : 0;
        setOpenOperationsCount(optionsOpenCount + managementOpenCount);
      } catch {
        if (active) {
          setOpenOperationsCount(0);
        }
      }
    };

    const handleRefresh = () => {
      loadOpenOperationsCount();
    };

    loadOpenOperationsCount();
    window.addEventListener(OPTIONS_OPEN_OPERATIONS_UPDATED_EVENT, handleRefresh);
    window.addEventListener('focus', handleRefresh);

    return () => {
      active = false;
      window.removeEventListener(OPTIONS_OPEN_OPERATIONS_UPDATED_EVENT, handleRefresh);
      window.removeEventListener('focus', handleRefresh);
    };
  }, [location.pathname, mode, user?.id]);

  const handleLogout = async () => {
    await signOut();
  };

  const handleEnterDemo = () => {
    enterDemoMode();
    setMobileOpen(false);
    navigate('/options');
  };

  const handleExitDemo = () => {
    exitDemoMode();
    setMobileOpen(false);
    navigate('/options');
  };

  const openUpgradeModal = () => {
    setMobileOpen(false);
    setShowUpgradeModal(true);
  };

  const handleOpenCryptoModal = (plan = null) => {
    setCryptoInitialPlan(plan);
    setShowUpgradeModal(false);
    setShowCryptoModal(true);
  };

  const handleCryptoBack = () => {
    setShowCryptoModal(false);
    setShowUpgradeModal(true);
  };

  const renderNavLink = (item, mobile = false) => {
    const isActive = item.match(location.pathname);
    return (
      <button
        type="button"
        key={`${mobile ? 'mobile' : 'desktop'}-${item.key}`}
        onClick={() => {
          setMobileOpen(false);
          navigate(item.to);
        }}
        data-testid={`options-nav-${item.key}${mobile ? '-mobile' : ''}`}
        className={`flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold font-body transition-colors ${mobile ? 'w-full' : ''} ${isActive ? 'bg-white text-black' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
        data-typography="nav"
      >
        <item.Icon size={16} weight="duotone" />
        <span>{item.label}</span>
        {item.badge ? (
          <span
            className={`rounded border px-1.5 py-0.5 text-[8px] font-bold uppercase ${isActive ? 'border-black/15 bg-black/10 text-black/65' : 'border-[#F0B90B]/25 bg-[#F0B90B]/10 text-[#F7D47A]'}`}
            data-testid={`options-nav-${item.key}-badge${mobile ? '-mobile' : ''}`}
          >
            {item.badge}
          </span>
        ) : null}
        {item.key === 'positions' ? (
          <span
            className={`inline-flex min-w-[22px] items-center justify-center rounded-md border px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${isActive ? 'border-black/15 bg-black/10 text-black/80' : 'border-white/10 bg-white/[0.04] text-white/72'}`}
            data-testid={`options-nav-positions-count${mobile ? '-mobile' : ''}`}
          >
            {openOperationsCount}
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <nav className="border-b border-white/10 bg-[#0A0A0A] font-body" data-testid="options-main-navigation">
      <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center justify-between gap-4 px-4 md:px-8">
        <div className="flex items-center gap-3" data-testid="options-brand-area">
          <Link to="/options" data-testid="options-brand-link">
            <img
              src={LOGO_HORIZONTAL}
              alt="GENI"
              className="h-10 w-auto object-contain transition-opacity hover:opacity-100 md:h-11 lg:h-12"
              data-testid="options-brand-logo"
            />
          </Link>
          {isDemoMode ? (
            <span className="hidden rounded-full border border-[#60A5FA]/25 bg-[#60A5FA]/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D6E8FF] lg:inline-flex" data-testid="options-product-demo-badge">
              {t('common.demoPortfolio')}
            </span>
          ) : null}
        </div>

        <div className="hidden items-center gap-1 lg:flex" data-testid="options-desktop-navigation-links">
          {items.map((item) => renderNavLink(item))}
        </div>

        <div className="flex items-center gap-2" data-testid="options-navigation-actions">
          <OptionsAlertsInboxButton notifications={alertsData?.notifications || []} unreadCount={alertsData?.summary?.unread_notifications || 0} onMarkRead={markRead} busy={busyRuleId !== null} />
          {!loading && !profile.isPro ? (
            <Button
              type="button"
              onClick={openUpgradeModal}
              className="hidden rounded-full border border-[#F0B90B]/35 bg-[#F0B90B]/14 px-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#F6D365] transition-colors hover:bg-[#F0B90B]/24 hover:text-[#FFE7A8] lg:inline-flex"
              data-testid="options-upgrade-cta"
            >
              UPGRADE PRO
            </Button>
          ) : null}
          <button
            type="button"
            onClick={toggleLanguage}
            className="inline-flex h-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] px-3 text-xs font-semibold uppercase tracking-[0.22em] text-white/75 transition-colors hover:bg-white/10 hover:text-white font-body"
            data-typography="nav"
            data-testid="options-language-toggle"
          >
            {language.toUpperCase()}
          </button>
          <div className="hidden lg:block">
            <ProductMenu user={user} profile={profile} onLogout={handleLogout} isDemoMode={isDemoMode} onEnterDemo={handleEnterDemo} onExitDemo={handleExitDemo} t={t} />
          </div>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/5 hover:text-white lg:hidden" data-testid="options-mobile-nav-trigger">
                <List size={22} weight="bold" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="overflow-y-auto border-white/10 bg-[#0A0A0A] text-white">
              <div className="space-y-6" data-testid="options-mobile-nav-sheet">
                <div className="space-y-2 pr-8">
                  <img
                    src={LOGO_HORIZONTAL}
                    alt="GENI"
                    className="h-10 w-auto object-contain md:h-11 lg:h-12"
                    data-testid="options-mobile-brand"
                  />
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid="options-mobile-profile-summary">
                    <p className="text-xs text-white/40">{t('common.signedInAs')}</p>
                    <p className="truncate text-sm font-medium text-white/80" data-testid="options-mobile-profile-email">{user?.email}</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#F0B90B]" data-testid="options-mobile-profile-plan">{profile.planLabel}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={toggleLanguage}
                  className="inline-flex w-fit items-center justify-center rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/75 transition-colors hover:bg-white/10 hover:text-white font-body"
                  data-typography="nav"
                  data-testid="options-language-toggle-mobile"
                >
                  {language.toUpperCase()}
                </button>

                <div className="space-y-2" data-testid="options-mobile-navigation-links">
                  {!loading && !profile.isPro ? (
                    <button
                      type="button"
                      onClick={openUpgradeModal}
                      className="w-full rounded-full border border-[#F0B90B]/35 bg-[#F0B90B]/12 px-4 py-3 text-left text-sm font-semibold uppercase tracking-[0.16em] text-[#F6D365] transition-colors hover:bg-[#F0B90B]/18"
                      data-testid="options-mobile-upgrade-cta"
                    >
                      UPGRADE PRO
                    </button>
                  ) : null}
                  {items.map((item) => renderNavLink(item, true))}
                </div>

                <div className="space-y-2 border-t border-white/10 pt-4" data-testid="options-mobile-product-switch-section">
                  <button onClick={() => { setMobileOpen(false); navigate('/dashboard'); }} className="w-full rounded-sm px-4 py-2 text-left text-sm font-semibold text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-mobile-switch-trades">
                    {t('nav.geniTrades')}
                  </button>
                  {isDemoMode ? (
                    <button onClick={handleExitDemo} className="w-full rounded-sm px-4 py-2 text-left text-sm font-semibold text-[#D6E8FF] transition-colors hover:bg-[#60A5FA]/10 hover:text-[#D6E8FF]" data-testid="options-mobile-exit-demo">
                      {t('nav.exitDemo')}
                    </button>
                  ) : (
                    <button onClick={handleEnterDemo} className="w-full rounded-sm px-4 py-2 text-left text-sm font-semibold text-[#D6E8FF] transition-colors hover:bg-[#60A5FA]/10 hover:text-[#D6E8FF]" data-testid="options-mobile-view-demo">
                      {t('nav.viewDemo')}
                    </button>
                  )}
                  <button onClick={() => { setMobileOpen(false); navigate('/billing'); }} className="w-full rounded-sm px-4 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-mobile-billing">
                    {t('common.billing')}
                  </button>
                  <button onClick={() => { setMobileOpen(false); navigate('/referral'); }} className="w-full rounded-sm px-4 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-mobile-referral">
                    {t('common.referral')}
                  </button>
                  <button onClick={() => { setMobileOpen(false); navigate('/settings'); }} className="w-full rounded-sm px-4 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-mobile-settings">
                    {t('common.settings')}
                  </button>
                  <button onClick={() => { setMobileOpen(false); navigate('/support'); }} className="w-full rounded-sm px-4 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-mobile-support">
                    {t('common.support')}
                  </button>
                  <button onClick={() => { setMobileOpen(false); navigate('/terms'); }} className="w-full rounded-sm px-4 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-mobile-terms">
                    {t('common.terms')}
                  </button>
                  <button onClick={() => { setMobileOpen(false); navigate('/privacy'); }} className="w-full rounded-sm px-4 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white" data-testid="options-mobile-privacy">
                    {t('common.privacy')}
                  </button>
                  <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="w-full rounded-sm px-4 py-2 text-left text-sm text-[#FF8A8A] transition-colors hover:bg-[#FF8A8A]/10 hover:text-[#FFB3B3]" data-testid="options-mobile-logout">
                    {t('common.logOut')}
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal && !profile.isPro}
        onClose={() => setShowUpgradeModal(false)}
        onSelectPlan={handleOpenCryptoModal}
        context="options"
        returnContext="options"
        content={optionsUpgradeContent}
        data-testid="options-upgrade-modal-instance"
      />

      <CryptoPaymentModal
        isOpen={showCryptoModal}
        onClose={() => setShowCryptoModal(false)}
        initialPlan={cryptoInitialPlan}
        onBack={handleCryptoBack}
      />
    </nav>
  );
};

export default OptionsNavigation;